import { action, batch } from '@reatom/framework'

import { pipe, Result } from '@blade/std-lib'
import { Network } from '@root/entities/network'
import { premiumMetaUpdateReceived } from '@root/entities/premium'
import { PremiumPurchase } from '@root/entities/premium/model'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'

import type { TypeHelper } from '@blade/std-lib'
import type { UserPurchaseHistoryListParams } from '@blade/trade-api-client'
import type { PremiumId } from '@root/entities/premium'

export const fetchUserPremiumCurrenciesRequestAction = action(
  async (ctx, { network }: { network: Network.Network }) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.user.userPremiumsList, [
        {
          network: Network.toDto(network),
        },
      ]),
    )
    const mappedResult = pipe(
      result,
      Result.mapError(ApiError.fallbackToUnknown),
    )

    batch(ctx, () => {
      if (Result.isSuccess(mappedResult)) {
        for (const currency of mappedResult.success) {
          premiumMetaUpdateReceived(ctx, {
            currency: currency,
            premiumMeta: currency.premium,
          })
        }
      }
    })

    return mappedResult
  },
  'fetchUserPremiumCurrenciesRequestAction',
)

export const checkIsUserHasPremiumRequestAction = action(
  async (ctx, { premiumId }: { premiumId: PremiumId }) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.premium.premiumEligibilityCreate, [
        {
          premiumId,
        },
      ]),
    )

    return result.pipe(Result.mapError(ApiError.fallbackToUnknown))
  },
  'checkIsUserHasPremiumRequestAction',
)

export const fetchUserPremiumPurchaseHistoryRequestAction = action(
  async (
    ctx,
    {
      network,
      ...req
    }: TypeHelper.OverrideKeys<
      UserPurchaseHistoryListParams,
      { network: Network.Network }
    >,
  ) => {
    const {
      tradeApiClient,
      environment: {
        assets: { tokenLogosHost },
      },
    } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.user.userPurchaseHistoryList, [
        {
          ...req,
          network: Network.toDto(network),
        },
      ]),
    )

    return result.pipe(
      Result.mapError(ApiError.fallbackToUnknown),
      Result.map((it) => ({
        ...it,
        data: it.data.map((dto) =>
          PremiumPurchase.fromDto(dto, tokenLogosHost),
        ),
      })),
    )
  },
  'fetchUserPremiumPurchaseHistoryRequestAction',
)
